import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { EnvironmentService } from '../../services/environment.service';
import { MsalInterceptorConfiguration, MsalService } from '@azure/msal-angular';
import { from, switchMap, catchError, EMPTY, throwError } from 'rxjs';
import { environment } from '../../../../environment/environment';
import { InteractionType } from '@azure/msal-browser';
import { isTokenExpired } from '../services/jwt.helper';

export const conditionalAuthInterceptor: HttpInterceptorFn = (req, next) => {
    const environmentService = inject(EnvironmentService);
    const isElectron = environmentService.isElectron();

    if (isElectron) {
        return _electronInterceptor(req, next);
    }

    const msalService = inject(MsalService);
    return _msalInterceptor(req, next, msalService);
};


function _electronInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn) {
    const isApiRequest = req.url.startsWith(environment.api) ||
        req.url.startsWith(environment.apiConfig.uri) ||
        req.url.startsWith(environment.apiADConfig.uri);

    if (!isApiRequest) {
        return next(req);
    }

    let authReq: HttpRequest<unknown>;
    if (navigator.onLine) {
        const token = localStorage.getItem('access_token');

        if (token && !isTokenExpired(token)) {
            authReq = req.clone({
                setHeaders: {
                    Authorization: `Bearer ${token}`
                }
            });
        } else if (token && isTokenExpired(token)) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('user_id');
            localStorage.removeItem('username');

            if (!req.url.includes('/api/auth/')) {
                fetch(`${environment.api}/api/auth/electron-login`)
                    .catch(console.error);
                return EMPTY;
            }
            authReq = req;
        } else if (!req.url.includes('/api/auth/')) {
            fetch(`${environment.api}/api/auth/electron-login`)
                .catch(console.error);
            return EMPTY;
        } else {
            authReq = req;
        }
    } else {
        authReq = req.clone({
            setHeaders: {
                'X-Auth-Mode': 'offline'
            }
        });
    }

    return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status === 401 && !req.url.includes('/api/auth/')) {
                const token = localStorage.getItem('access_token');
                if (token && isTokenExpired(token)) {
                    localStorage.removeItem('access_token');
                    localStorage.removeItem('user_id');
                    localStorage.removeItem('username');

                    fetch(`${environment.api}/api/auth/electron-login`)
                        .catch(console.error);
                    return EMPTY;
                }
            }

            return throwError(() => error);
        })
    );
}

function _msalInterceptor(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    msalService: MsalService
) {
    const msalInterceptorConfig = MSALInterceptorConfigFactory();
    const protectedResourceMap = msalInterceptorConfig.protectedResourceMap;
    let scopes: string[] | null = null;

    for (const [url, urlScopes] of protectedResourceMap.entries()) {
        if (req.url.startsWith(url)) {
            scopes = urlScopes?.flatMap(scope =>
                typeof scope === 'string' ? scope : (scope.scopes || [])
            ) || [];
            break;
        }
    }

    if (!scopes) {
        return next(req);
    }

    const account = msalService.instance.getActiveAccount();

    if (!account) {
        if (msalInterceptorConfig.interactionType === InteractionType.Redirect) {
            msalService.loginRedirect({
                scopes: scopes
            });
        }
        return EMPTY;
    }

    // Acquire token silently
    return from(msalService.acquireTokenSilent({
        scopes: scopes,
        account: account
    })).pipe(
        switchMap(result => {
            const authReq = req.clone({
                setHeaders: {
                    Authorization: `Bearer ${result.accessToken}`
                }
            });
            return next(authReq).pipe(
                catchError((error: HttpErrorResponse) => {
                    // Let "not registered" and "forbidden" errors propagate to noAccessInterceptor
                    if (error.status === 401 && error?.error?.error === 'User is not registered in SizingTools.') {
                        return throwError(() => error);
                    }
                    if (error.status === 403) {
                        return throwError(() => error);
                    }
                    // Handle other 401 errors from API responses (not authenticated)
                    if (error.status === 401) {
                        console.warn('Authentication error from API, redirecting to login...', error);
                        msalService.loginRedirect({
                            scopes: scopes || []
                        });
                        return EMPTY;
                    }
                    return throwError(() => error);
                })
            );
        }),
        catchError(error => {
            const isAuthError = error?.errorCode?.includes('interaction_required') ||
                error?.errorCode?.includes('consent_required') ||
                error?.errorCode?.includes('login_required') ||
                error?.errorCode?.includes('monitor_window_timeout') ||
                error?.errorCode?.includes('token_renewal_error');

            if (isAuthError && msalInterceptorConfig.interactionType === InteractionType.Redirect) {
                console.warn('Token acquisition failed, redirecting to login...', error);
                msalService.loginRedirect({
                    scopes: scopes || []
                });
                return EMPTY;
            }

            return throwError(() => error);
        })
    );
}

function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, string[] | null>();
    protectedResourceMap.set(environment.apiConfig.uri, environment.apiConfig.scopes);
    protectedResourceMap.set(environment.apiADConfig.uri, environment.apiADConfig.scopes);
    protectedResourceMap.set(environment.api, environment.apiConfig.scopes);

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap,
    };
}